import { useEffect, useId, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { IconKusovy, IconOperacia } from "@/components/ui/tandem-icons";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDeleteDielec, useUpdateDielec } from "@/api/hooks/useDielce";
import {
  hrubkaMaterialu,
  hrubkyPreDielec,
  useDostupneHrubky,
  type DostupnaHrubka,
} from "@/api/hooks/useDostupneHrubky";
import { useSplitRules } from "@/api/hooks/useSplitRules";
import { useCenaOverrideMap } from "@/api/hooks/useZakazkaCenaOverrides";
import { pravidloPreHrubku } from "@/lib/splitRules";
import { LepenyConvertDialog } from "@/components/dielec/LepenyConvertDialog";
import { PridatOperaciuDialog } from "@/components/dielec/PridatOperaciuDialog";
import { KovanieChangeDialog } from "@/components/dielec/KovanieChangeDialog";
import { VlastnaDetailBody } from "@/components/dielec/VlastnaDetailBody";
import { formatKategoria } from "@/lib/kategorie";
import {
  EdgesUsageSection,
  KovaniaUsageSection,
} from "@/components/zakazka/UsageSummarySections";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { useNastaveniaGlobal } from "@/api/hooks/useNastaveniaGlobal";
import {
  PrirezLegenda,
  PrirezVisual,
} from "@/components/dielec/PrirezVisual";
import { cncStav, dielecRotovat, type CncUpnutie } from "@/lib/cncPrirez";
import { AlertTriangle, ClipboardPaste, Copy, RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  defaultHranyState,
  HranaPickerDialog,
  type HranyState,
} from "@/components/dielec/HranyEditor";
import {
  PridaneHranyDialog,
  type VyberPridanejHrany,
} from "@/components/dielec/PridaneHranyDialog";
import { pridaneHranyPodtext } from "@/lib/pridaneHrany";
import {
  hranyDoUpdate,
  hranySaZmenili,
  toHrany,
  toZaoblenie,
  zaoblenieDoUpdate,
  zaoblenieSaZmenilo,
  type ZaoblenieState,
} from "@/lib/hrany";
import { DielecVisual } from "@/components/dielec/DielecVisual";
import {
  hranyZoSchranky,
  nastavSchrankuHran,
  schrankaZHran,
  useSchrankaHran,
} from "@/lib/prenosHran";
import {
  formatEur,
  materialPrimaryId,
  menoKovania,
  normalizeNum,
  parseDecimal,
  zobrazRozmer,
} from "@/lib/format";
import { PravyNazov } from "@/components/cennik/PravyNazov";
import { setLastSlot } from "@/lib/lastSlot";
import { slotyPreDielec } from "@/lib/druhSlotu";
import { useOperacie } from "@/api/hooks/useOperacie";
import {
  OPERACIA_LEPENA_VRSTVA,
  jeLepiaciPostup,
  jeOperaciaRiadok,
  jePostup,
  postupPopis,
} from "@/lib/operacie";
import { useNazvyDielcov } from "@/api/hooks/useNazvyDielcov";
import {
  maHolyRozmer,
  rozmerNaUlozenie,
  zobrazenyRozmer,
  type PoleRozmeru,
} from "@/lib/rozmer";
import {
  nadmieraNaUlozenie,
  posunNadmiery,
  zobrazenaNadmiera,
} from "@/lib/nadmiera";
import type {
  Dielec,
  DielecUpdate,
  MaterialSlot,
  SplitRule,
} from "@/api/schemas";

/**
 * Výškový rozpočet pásu detailu (KOLO 66) — čísla, ktoré musia sedieť, inak
 * začne stĺpec nákresu scrollovať práve tam, kde sa klikajú hrany.
 *
 * Nedá sa to overiť layoutom (jsdom výšky nemeria), preto sú to konštanty
 * a `DielecDetailPanel.test.tsx` kontroluje, že
 * `NAKRES_H + HLAVICKA_H + NAKRES_PADDING_Y + 2 <= PAS_KARTA_H` s rezervou.
 * Kto bude nákres zväčšovať, dozvie sa to z padajúceho testu, nie od usera.
 */
/** Karta pásu: obal `h-[325px]` mínus `pb-3` 12 (KOLO 63). */
export const PAS_KARTA_H = 313;
/** Hlavička: label `h-4` 16 + pole `h-6` 24 + `py-1.5` 12 + `border-b` 1. */
export const HLAVICKA_H = 53;
/** `py-2` stĺpca nákresu. */
export const NAKRES_PADDING_Y = 16;
/** Bordery karty (`rounded-lg border`) — hore aj dole. */
export const KARTA_BORDER = 2;

/**
 * Detail panel selected dielca — 4 sekcie inšpirované DaexCUT screenshot 08:
 *
 * 1. IDENTIFIKÁCIA — ID dielca, kód, popis
 * 2. PARAMETRY — množstvo, rozmery (X/Y/Z + nadmiery), smer vlákien,
 *    Hrany (X1, X2, Y1, Y2) cez HranyEditor
 * 3. MATERIÁL PRVKU — info o materiáli (cez slot), rozmery dosky
 * 4. MATERIÁLY V ZÁKAZCE — zoznam slotov; klik = priradí slot k dielcu
 *
 * Auto-save: zmena fieldu → 400ms debounce → PATCH /api/dielce/{id}.
 */
export interface DielecDetailPanelProps {
  zakazkaId: string;
  dielec: Dielec | null;
  sloty: MaterialSlot[];
  /** Lepené vrstvy vybraného dielca (dielec je rodič). */
  vrstvy?: Dielec[];
  /** Súčasti skla (kalenie, vŕtanie, služby) — deti sklo dielca. */
  sucasti?: Dielec[];
  /** Otvorí kalkulačku skla v edit móde pre vybraný sklo dielec. */
  onEditSklo?: () => void;
  /**
   * Zámok (KOLO 231) — prečo sa dielec nedá upravovať (obchodník
   * v nábytku mení cenu riadku v paneli zostavy). Detail sa len ukáže.
   */
  zamok?: string | null;
}

/** Zastaví klik skôr, než dôjde k ovládaniu pod ním (hrany v náčrte…). */
function zastavKlik(e: React.SyntheticEvent): void {
  e.preventDefault();
  e.stopPropagation();
}

export function DielecDetailPanel({
  zamok = null,
  ...props
}: DielecDetailPanelProps): JSX.Element {
  const obsah = <DielecDetailObsah {...props} />;
  if (!zamok || !props.dielec) return obsah;
  // Len na čítanie: `fieldset disabled` vypne polia, výbery aj tlačidlá
  // všetkých typov detailu naraz a zachytenie kliku aj klikateľné strany
  // náčrtu hrán — autosave tak nemá čo uložiť
  return (
    <div
      className="h-full"
      title={zamok}
      data-testid="dielec-detail-zamok"
      onClickCapture={zastavKlik}
      onDoubleClickCapture={zastavKlik}
    >
      <fieldset disabled className="contents">
        {obsah}
      </fieldset>
    </div>
  );
}

function DielecDetailObsah({
  zakazkaId,
  dielec,
  sloty,
  vrstvy = [],
  sucasti = [],
  onEditSklo,
}: Omit<DielecDetailPanelProps, "zamok">): JSX.Element {
  if (!dielec) {
    // Výšku vlastní obal pásu (KOLO 63) — `h-full`, nie vlastných 313 px.
    // Vetvy s hard výškou mali 313, vetvy „hlavička + lg:h-[260px]" 315
    // (bordery karty), takže tabuľka pri prekliku poskočila o 2 px.
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-dashed bg-card text-center text-sm text-muted-foreground">
        <div>
          <Pencil className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-2">
            Klikni na dielec v tabuľke pre zobrazenie detailu a editáciu.
          </p>
        </div>
      </div>
    );
  }

  // Sklo (rodič z kalkulačky) — parametre a súčasti sa menia cez dialóg
  // Upraviť sklo. Hrany/nadmiera/operácia pre sklo nemajú zmysel.
  if (
    dielec.material_direct?.kategoria === "SKLO" &&
    !dielec.parent_dielec_id
  ) {
    return (
      <SkloDetailBody
        zakazkaId={zakazkaId}
        dielec={dielec}
        sloty={sloty}
        sucasti={sucasti}
        onEditSklo={onEditSklo}
      />
    );
  }

  // Vlastná položka mimo cenníka (KOLO 210) — cenu nesie riadok
  if (dielec.je_vlastna_polozka) {
    return <VlastnaDetailBody zakazkaId={zakazkaId} dielec={dielec} />;
  }

  // Kovanie (kusový direct materiál) — zredukovaný panel: len názov,
  // kusy a zmena kovania. Rozmery/hrany/nadmiera nemajú zmysel.
  if (dielec.material_direct?.druh === "kusovy") {
    return (
      <KovanieDetailBody zakazkaId={zakazkaId} dielec={dielec} sloty={sloty} />
    );
  }

  // Operácia (vnorená pod dielcom aj paušál zostavy ako „Ostatné") —
  // zredukovaný panel bez hrán, nadmiery a materiálov.
  if (jeOperaciaRiadok(dielec)) {
    return <OperaciaDetailBody zakazkaId={zakazkaId} dielec={dielec} />;
  }

  return (
    <DetailBody
      zakazkaId={zakazkaId}
      dielec={dielec}
      sloty={sloty}
      vrstvy={vrstvy}
    />
  );
}

interface DetailFields {
  nazov_strojovy: string;
  pocet_ks: string;
  x_mm: string;
  y_mm: string;
  z_mm: string;
  nadmiera_aktivna: boolean;
  nadmiera_auto: boolean;
  nadmiera_x_mm: string;
  nadmiera_y_mm: string;
  /** Rotovať CNC dielec kvôli čelám: null = auto podľa názvu. */
  cnc_rotovat: boolean | null;
  /** Ide na CNC: null = odvoď z príznaku výrobného postupu (`je_cnc`). */
  ide_na_cnc: boolean | null;
  /** Operácia (kod/label) alebo voľný text — preloží sa v buildUpdate. */
  operacia_text: string;
  smer_vlakien: string;
}

function DetailBody({
  zakazkaId,
  dielec,
  sloty,
  vrstvy,
}: {
  zakazkaId: string;
  dielec: Dielec;
  sloty: MaterialSlot[];
  vrstvy: Dielec[];
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const deleteMutation = useDeleteDielec(zakazkaId);

  const [fields, setFields] = useState<DetailFields>(() => toFields(dielec));
  const [hrany, setHrany] = useState<HranyState>(() => toHrany(dielec));
  // Schránka hrán (KOLO 177) — skopírované strany a typy z iného dielca
  const schranka = useSchrankaHran();
  const [vkladam, setVkladam] = useState(false);

  async function vlozHrany(): Promise<void> {
    if (!schranka) return;
    setVkladam(true);
    try {
      const r = await hranyZoSchranky(schranka, parseFloat(fields.z_mm) || null);
      if ("chyba" in r) {
        toast({
          variant: "destructive",
          title: `Hrany z ${schranka.kod ?? "dielca"} sa nedali vložiť`,
          description: r.chyba,
        });
        return;
      }
      // Lokálny stav — uloží ho autosave ako ručnú zmenu hrán
      setHrany(r.hrany);
      toast({ title: `Hrany z ${schranka.kod ?? "dielca"} vložené` });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Hrany sa nedali vložiť",
        description: extractErrorMessage(err),
      });
    } finally {
      setVkladam(false);
    }
  }
  // Zaoblené strany masívu (KOLO 168) — ukladajú sa s ostatnými poliami
  const [zaoblenie, setZaoblenie] = useState<ZaoblenieState>(() =>
    toZaoblenie(dielec),
  );
  const [slotId, setSlotId] = useState<string | null>(dielec.material_slot_id ?? null);
  // Ponuka slotov podľa druhu uloženého slotu (KOLO 226) — nie podľa
  // práve kliknutého, inak by sa zoznam po kliku prepol
  const povodnySlot = sloty.find((s) => s.id === dielec.material_slot_id) ?? null;

  // Pending save — rozpracované zmeny ktoré debounce ešte neodoslal.
  // Flush pri prepnutí na iný dielec / unmounte, inak by sa TICHO stratili.
  const pendingRef = useRef<{ id: string; data: DielecUpdate } | null>(null);
  const mutateRef = useRef(updateMutation.mutate);
  useEffect(() => {
    mutateRef.current = updateMutation.mutate;
  });

  // Flush pending save STARÉHO dielca pri zmene dielec.id a pri unmounte.
  // Fire-and-forget (mutate) — payload aj ID berieme z ref, nie z props.
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) mutateRef.current(pending);
    };
  }, [dielec.id]);

  // Re-sync keď sa zmení selected dielec (klik na iný riadok / refetch)
  useEffect(() => {
    setFields(toFields(dielec));
    setHrany(toHrany(dielec));
    setZaoblenie(toZaoblenie(dielec));
    setSlotId(dielec.material_slot_id ?? null);
  }, [dielec]);

  // Auto-save 400ms debounce; pendingRef drží aktuálny neodoslaný payload.
  // `dielec` je v deps zámerne — po re-synci (isDirty=false) sa pending
  // vyčistí a starý timer zruší, takže save → invalidate → refetch necykluje.
  // Prepínač zákazky „rozmery bez hrán" (KOLO 131) — ručne zadané X/Y
  // sa posielajú ako holý rozmer, backend pripočíta hrany.
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  const rozmeryBezHran = zakazkaQuery.data?.data?.rozmery_bez_hran ?? false;
  const jeVrstva =
    !!dielec.parent_dielec_id && dielec.operacia === OPERACIA_LEPENA_VRSTVA;

  useEffect(() => {
    if (!isDirty(dielec, fields, hrany, slotId, zaoblenie)) {
      pendingRef.current = null;
      return undefined;
    }
    const pending = {
      id: dielec.id,
      data: buildUpdate(dielec, fields, hrany, slotId, zaoblenie, {
        bezHran: rozmeryBezHran,
        jeVrstva,
      }),
    };
    pendingRef.current = pending;
    const t = setTimeout(() => {
      pendingRef.current = null;
      updateMutation.mutateAsync(pending).catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní",
          description: extractErrorMessage(err),
        });
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dielec, fields, hrany, slotId, zaoblenie]);

  const currentSlot = sloty.find((s) => s.id === slotId) ?? null;
  const currentMaterial = currentSlot?.material ?? null;
  // Dĺžkový riadok (hranol, cenovka) nesie materiál priamo, nie cez slot —
  // do KOLA 169 tu svietilo „Žiadny materiál priradený"
  const materialPrvku =
    currentMaterial ?? (slotId === null ? (dielec.material_direct ?? null) : null);
  // Cenovka z hrany (KOLO 169) — hrana sa mení cez picker hrán
  const riadokZHrany = !!dielec.riadok_z_hrany && slotId === null;
  const [zmenaHrany, setZmenaHrany] = useState(false);
  // Pridané hrany (olepenie otvoru) — len dielec, ktorý ich smie mať (backend)
  const pridane = dielec.pridane_hrany ?? [];
  const [pridaneOpen, setPridaneOpen] = useState(false);
  // Smer vlákien dáva zmysel len pri materiáli s drevnou štruktúrou
  // (smer_vlakien_default ≠ "0"); bez materiálu nechaj prepínač voľný.
  const materialDielca = currentMaterial ?? dielec.material_direct ?? null;
  const maStrukturu = materialDielca
    ? materialDielca.smer_vlakien_default !== "0"
    : true;
  // Masív sa neolepuje (KOLO 168) — rozhoduje backend; materiál vybraného
  // slotu má prednosť, aby sa nákres prepol hneď po kliknutí na slot
  const masiv = materialDielca ? !!materialDielca.masiv : !!dielec.masiv;
  useEffect(() => {
    if (!maStrukturu && fields.smer_vlakien !== "0") {
      setFields((f) => ({ ...f, smer_vlakien: "0" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maStrukturu, fields.smer_vlakien]);
  const hrubky = useDostupneHrubky();
  // Pole Operácia = len kategórie práce (nc, olep_nc…); platené operácie
  // sa pridávajú ako vnorené riadky cez PridatOperaciuDialog.
  // Výrobné postupy — `jePostup`, nie `systemove: true`: postup založený
  // userom je `je_systemova=False` a v zozname by chýbal (KOLO 108).
  const operacieQuery = useOperacie();
  const operacie = (operacieQuery.data?.data ?? []).filter(jePostup);
  // Rodič lepených vrstiev — ponúka len postupy s lepením (KOLO 138)
  const jeSendvic = vrstvy.some((v) => v.operacia === OPERACIA_LEPENA_VRSTVA);
  const [opDialogOpen, setOpDialogOpen] = useState(false);
  // Prednastavené názvy dielcov — datalist pre Názov (hrany aplikuje backend)
  const nazvyQuery = useNazvyDielcov();
  const nazvyDielcov = nazvyQuery.data?.data ?? [];
  const splitRulesQuery = useSplitRules();
  const splitRules = splitRulesQuery.data?.data ?? [];
  // Lepená hrúbka vybraná v Z selecte → dialóg konverzie na vrstvy
  const [lepeny, setLepeny] = useState<{ rule: SplitRule; z: string } | null>(null);

  function handleZmenaHrubky(v: string): void {
    // Vrstva (_lepN) — hrúbku mení priamo, bez split dialógu
    if (dielec.parent_dielec_id) {
      setFields({ ...fields, z_mm: v });
      return;
    }
    if (parseFloat(v) === parseFloat(fields.z_mm || "")) return;
    // Skutočná hrúbka lepeného (37,4) patrí k pravidlu 36 (KOLO 188)
    const rule = pravidloPreHrubku(splitRules, v);
    if (rule) {
      setLepeny({ rule, z: v });
      return;
    }
    if (!rule && vrstvy.length > 0) {
      if (
        !window.confirm(
          `Dielec ${dielec.kod} má lepené vrstvy — zrušiť ich a nastaviť ${v} mm?`,
        )
      ) {
        return;
      }
      void (async () => {
        for (const vr of vrstvy) {
          await deleteMutation.mutateAsync(vr.id);
        }
      })();
    }
    setFields({ ...fields, z_mm: v });
  }
  const nazovListId = useId();
  // Celkové rozmery s nadmierou (celková na rozmer, nie na stranu):
  // celkové X = x + nadmiera_x
  // Pri holom rozmere je v poli doska bez hrán — celkový rozmer sa ráta
  // z rozmeru s hranou, preto sa k poľu pripočíta hrúbka hrán v osi.
  const xNum = parseFloat(fields.x_mm) + hranyVOsi(dielec, "x_mm");
  const yNum = parseFloat(fields.y_mm) + hranyVOsi(dielec, "y_mm");
  // V poli je nadmiera tak, ako ju číta dielňa (KOLO 133) — k rozmeru
  // s hranou sa pripočíta uložená, teda zobrazená − posun.
  const nadX = (parseFloat(fields.nadmiera_x_mm) || 0) - posunNadmiery(dielec, "x");
  const nadY = (parseFloat(fields.nadmiera_y_mm) || 0) - posunNadmiery(dielec, "y");
  const totalX =
    fields.nadmiera_aktivna && Number.isFinite(xNum) && xNum > 0
      ? xNum + nadX
      : null;
  const totalY =
    fields.nadmiera_aktivna && Number.isFinite(yNum) && yNum > 0
      ? yNum + nadY
      : null;
  // CNC prírez — minimum upnutia (predrez), združenie, rotácia kvôli
  // čelám. Jeden zdroj pravdy (cncStav) zrkadlí backend, aby bolo JASNE
  // vidno, aký hrubý rozmer dielec reálne dostane.
  const nastaveniaGlobal = useNastaveniaGlobal();
  const gn = nastaveniaGlobal.data?.data;
  const cnc: CncUpnutie | null = gn
    ? {
        minDlzkaMm: gn.cnc_upnutie_min_dlzka_mm ?? 0,
        minSirkaMm: gn.cnc_upnutie_min_sirka_mm ?? 0,
        zdruzenieMedzeraMm: gn.cnc_zdruzenie_medzera_mm ?? 10,
        rotaciaMaxMm: gn.cnc_rotacia_max_mm ?? 0,
        nazvy: {
          rez: gn.cnc_nazov_rez,
          zdruz_rez: gn.cnc_nazov_zdruz_rez,
          rot_rez: gn.cnc_nazov_rot_rez,
          rot_zdruz_rez: gn.cnc_nazov_rot_zdruz_rez,
        },
      }
    : null;
  const pocetKsNum = parseInt(fields.pocet_ks, 10) || 1;
  // Rozmer na dorezanie (KOLO 195) počíta server z predfrezu postupu
  // a hrán — platí, kým rozmer v poli sedí s uloženým, inak čistý rozmer
  const ulozenyRozmer =
    xNum === Number(dielec.x_mm) && yNum === Number(dielec.y_mm);
  const cncInfo =
    Number.isFinite(xNum) && xNum > 0 && Number.isFinite(yNum) && yNum > 0
      ? cncStav({
          xMm: xNum,
          yMm: yNum,
          nadmieraXMm: nadX,
          nadmieraYMm: nadY,
          nadmieraAktivna: fields.nadmiera_aktivna,
          pocet: pocetKsNum,
          operaciaJeCnc:
            operacie.find((o) => o.kod === fields.operacia_text)?.je_cnc ??
            null,
          nazovStrojovy: fields.nazov_strojovy,
          cncRotovat: fields.cnc_rotovat,
          ideNaCnc: fields.ide_na_cnc,
          cnc,
          rezXMm:
            ulozenyRozmer && dielec.rez_x_mm != null ? Number(dielec.rez_x_mm) : null,
          rezYMm:
            ulozenyRozmer && dielec.rez_y_mm != null ? Number(dielec.rez_y_mm) : null,
        })
      : null;
  const rotovat = dielecRotovat(fields.nazov_strojovy, fields.cnc_rotovat);
  // Karta Prírez sa ukazuje len pri dielcoch s CNC operáciou
  const maPrirezNahlad = (cncInfo?.oznacenie ?? "") !== "";
  // Dôvod, ktorý treba vysvetliť dielni. Neutrálna nadmiera nie je dôvod —
  // tá dielca nijako neprekvapí, takže sa nekomentuje.
  const cncMaDovod =
    cncInfo != null &&
    cncInfo.dovod !== "ziadny" &&
    cncInfo.dovod !== "nadmiera";
  // Hrubý prírez v zobrazenom poradí podľa smeru vlákien (Y → prehodené)
  const [hrubyPrvy, hrubyDruhy] = cncInfo
    ? zobrazRozmer(cncInfo.hrubyX, cncInfo.hrubyY, fields.smer_vlakien)
    : [0, 0];
  const hrubaHranaMm = zakazkaQuery.data?.data?.hruba_hrana_mm
    ? String(zakazkaQuery.data.data.hruba_hrana_mm)
    : undefined;
  // Pridaná hrana z hrán, ktoré používame (KOLO 196) — materiál dosky zo
  // slotu, inak priama doska; navrchu hrany zo strán dielca
  const materialPridanejHrany =
    currentSlot?.material_id ??
    (dielec.material_direct?.druh === "plosny" ? dielec.material_id_direct : null);
  const vyberPridanejHrany: VyberPridanejHrany | null = materialPridanejHrany
    ? {
        materialId: materialPridanejHrany,
        slotId: currentSlot?.id,
        variantaId: sloty[0]?.varianta_id,
        hrubaHranaMm,
        zMm: parseFloat(fields.z_mm) || undefined,
        naDielci: [
          dielec.hrana_x1_id,
          dielec.hrana_x2_id,
          dielec.hrana_y1_id,
          dielec.hrana_y2_id,
        ].filter((id): id is string => !!id),
      }
    : null;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border bg-card">
      {/* Hlavičkový pás — identifikácia + rozmery v jednom riadku.
          items-start: všetky polia (label + input) v jednej rovine;
          nadmiera pod X/Y visí dole bez posúvania ostatných.
          `shrink-0`: výšku karty určuje obal pásu, hlavička si berie svoje
          a telo dostane zvyšok (KOLO 63). */}
      <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1 border-b bg-background-alt/40 px-4 py-1.5">
        <div>
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">Kód</div>
          <div className="font-mono text-sm font-semibold leading-6">
            {dielec.kod ?? "—"}
          </div>
        </div>
        <div>
          <Label
            htmlFor="d-nazov"
            className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
          >
            Názov strojový
          </Label>
          <Input
            id="d-nazov"
            list={nazovListId}
            value={fields.nazov_strojovy}
            onChange={(e) =>
              setFields({ ...fields, nazov_strojovy: e.target.value })
            }
            placeholder="bok_L"
            className="h-6 w-44 text-xs font-mono px-1.5"
          />
          <datalist id={nazovListId}>
            {nazvyDielcov.map((n) => (
              <option key={n.id} value={n.nazov} />
            ))}
          </datalist>
        </div>
        <div className="w-14">
          <NumField
            label="Ks"
            value={fields.pocet_ks}
            onChange={(v) => setFields({ ...fields, pocet_ks: v })}
          />
        </div>
        {/* Polia X a Y v poradí PODĽA SMERU VLÁKIEN — pri „Y" sa dielec
            reže otočený, tak sa aj rozmery čítajú prehodené (300 × 500).
            Väzba na x_mm/y_mm ostáva správna (labely X/Y sa nemenia). */}
        {(fields.smer_vlakien === "Y" ? ["y", "x"] : ["x", "y"]).map(
          (os) =>
            os === "x" ? (
              <div className="w-28" key="pole-x">
                <NumField
                  label="X"
                  value={fields.x_mm}
                  onChange={(v) => setFields({ ...fields, x_mm: v })}
                  hint={rozmerPoOlepeni(dielec, "x_mm")}
                  extra={
                    fields.nadmiera_aktivna ? (
                      <NadmieraMini
                        ariaLabel="Nadmiera X"
                        value={fields.nadmiera_x_mm}
                        onChange={(v) =>
                          // Ručný prepis uzamkne hodnotu — inak by ju
                          // ďalšie uloženie prepočítalo z hrán naspäť.
                          setFields({
                            ...fields,
                            nadmiera_x_mm: v,
                            nadmiera_auto: false,
                          })
                        }
                        total={totalX}
                        auto={fields.nadmiera_auto}
                      />
                    ) : (
                      <span />
                    )
                  }
                />
              </div>
            ) : (
              <div className="w-28" key="pole-y">
                <NumField
                  label="Y"
                  value={fields.y_mm}
                  onChange={(v) => setFields({ ...fields, y_mm: v })}
                  hint={rozmerPoOlepeni(dielec, "y_mm")}
                  extra={
                    fields.nadmiera_aktivna ? (
                      <NadmieraMini
                        ariaLabel="Nadmiera Y"
                        value={fields.nadmiera_y_mm}
                        onChange={(v) =>
                          // Ručný prepis uzamkne hodnotu — inak by ju
                          // ďalšie uloženie prepočítalo z hrán naspäť.
                          setFields({
                            ...fields,
                            nadmiera_y_mm: v,
                            nadmiera_auto: false,
                          })
                        }
                        total={totalY}
                        auto={fields.nadmiera_auto}
                      />
                    ) : (
                      <span />
                    )
                  }
                />
              </div>
            ),
        )}
        {materialDielca?.druh !== "dlzkovy" ? (
          <div className="w-28">
            {/* Len hrúbky, ktoré materiál dovolí (KOLO 162) — z 16 mm nie 36 */}
            <HrubkaField
              value={fields.z_mm}
              hrubky={hrubkyPreDielec(
                hrubky,
                splitRules,
                dielec.parent_dielec_id ? null : hrubkaMaterialu(materialDielca),
                { lepene: !dielec.parent_dielec_id },
              )}
              onChange={handleZmenaHrubky}
            />
          </div>
        ) : null}
        {/* Schránka hrán (KOLO 177) — dve ikony, rezerva hlavičky je 161 px */}
        {!masiv ? (
          <div className="ml-auto flex items-center gap-1 self-end pb-0.5">
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="h-7 w-7"
              aria-label="Kopírovať hrany"
              title="Kopírovať hrany — strany a typy hrán vložíš inému dielcu (alebo viacerým v hromadnej úprave)"
              onClick={() => {
                nastavSchrankuHran(schrankaZHran(dielec.id, dielec.kod, hrany));
                toast({ title: `Hrany ${dielec.kod ?? ""} skopírované` });
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            {schranka && schranka.dielecId !== dielec.id ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7"
                aria-label={`Vložiť hrany z ${schranka.kod ?? "dielca"}`}
                title={`Vložiť hrany z ${schranka.kod ?? "dielca"} — rovnaké strany a typ, šírka podľa tejto dosky`}
                disabled={vkladam}
                onClick={() => void vlozHrany()}
              >
                <ClipboardPaste className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        ) : null}
        {/* Triedenie, checkbox Nadmiery a Rotácia (čelá) sú od KOLA 67
            v sekcii „Spracovanie" v materiálovom stĺpci. Hlavička v stĺpci
            tabuľky (864 px) potrebovala na jeden riadok 947 px a zalamovala
            sa; bez nich má 703 px, teda 161 px rezervy. Zostáva tu to, čo sa
            mení pri každom dielci: Kód, Názov, Ks, X, Y, Hrúbka. */}
        {/* Pole „Prírez (hrubý)" tu bolo tretím výskytom toho istého čísla
            (diagram `sNadmierou` + karta Prírez) — zmazané, KOLO 49. */}
      </div>

      {/* CNC upozornenie tu bolo ako celoširoký pás — presunuté do karty
          PRÍREZ k vizuálu (KOLO 49). Ušetrí ~53 px a panel už neposkakuje
          podľa toho, či dielec ide na CNC. */}

      {/* Telo: diagram | materiál + sloty | hrany + kovania v zákazke.
          Výšku berie zo zvyšku karty (`flex-1`), nie z vlastných 260 px —
          pod `lg` sú tracky pod sebou, takže telo musí scrollovať vnútri,
          inak by pás rástol na výšku obsahu (KOLO 63). */}
      <div
        className={cn(
          "grid min-h-0 flex-1 grid-cols-1 divide-y overflow-y-auto lg:divide-x lg:divide-y-0 lg:overflow-hidden",
          // Karta Prírez je druhý track — pridáva sa len keď existuje,
          // inak by posledný stĺpec spadol do prázdneho tracku.
          // Prírez má PEVNÚ šírku. Pri `minmax(250px,auto)` rástol track na
          // max-content dlhého CNC textu a materiálový stĺpec sa zdrbol.
          // Štvrtý track (hrany/kovania v zostave) až od `2xl` — pás je od
          // KOLA 67 späť v stĺpci tabuľky, kde má telo karty 894 px:
          // 412 (nákres) + 260 (prírez) + 250 = 922 > 894, takže by materiál
          // dostal 0 a karta by pretiekla. Bez štvrtého tracku má materiál
          // 222 px. Pri `2xl` má stĺpec 1200 px a zmestia sa všetky štyri
          // (materiálu 276 px). KOLO 63d ho malo na `xl`, ale to platilo pre
          // pás cez plnú šírku (1232 px).
          maPrirezNahlad || cncMaDovod
            ? "lg:grid-cols-[auto_260px_minmax(0,1fr)] 2xl:grid-cols-[auto_260px_minmax(0,1fr)_250px]"
            : "lg:grid-cols-[auto_minmax(0,1fr)] 2xl:grid-cols-[auto_minmax(0,1fr)_250px]",
        )}
      >
        {/* 1. Diagram s hranami — pevná šírka, žiadne prázdne boky.
            `overflow-hidden`, NIE `overflow-y-auto` (KOLO 66): tu sa klikajú
            hrany a scrollbar tu nemá čo robiť. Do KOLA 66 tu `auto` bolo a
            keďže rezerva výšky bola 3 px, u usera sa objavil zvislý
            scrollbar — a ten spustil kaskádu: CSS pri `overflow-y: auto`
            vynúti aj `overflow-x: auto`, zvislý scrollbar zožral ~15 px
            šírky a pribudol aj vodorovný. Navyše scrollovací box má
            automatické minimum 0, takže grid track `auto` sa dal stlačiť
            pod obsah. Rozpočet výšky drží `NAKRES_H` + test nižšie. */}
        <div className="flex items-center justify-center overflow-hidden px-4 py-2">
          <DielecVisual
            xMm={parseFloat(fields.x_mm) || 0}
            yMm={parseFloat(fields.y_mm) || 0}
            nazov={fields.nazov_strojovy}
            smerVlakien={fields.smer_vlakien}
            maStrukturu={maStrukturu}
            hrany={hrany}
            onHranyChange={setHrany}
            masiv={masiv}
            zaoblenie={zaoblenie}
            onZaoblenieChange={setZaoblenie}
            stavHran={dielec.stav_hran ?? undefined}
            onSmerChange={(s) => setFields({ ...fields, smer_vlakien: s })}
            slotId={slotId}
            slotMaterialId={currentSlot?.material_id ?? null}
            hrubaHranaMm={hrubaHranaMm}
            variantaId={sloty[0]?.varianta_id ?? null}
            zMm={parseFloat(fields.z_mm) || 18}
            sNadmierou={
              cncInfo != null && cncInfo.cncDielec
                ? `${normalizeNum(hrubyPrvy)} × ${normalizeNum(hrubyDruhy)}`
                : totalX != null && totalY != null
                  ? `${normalizeNum(totalX)} × ${normalizeNum(totalY)}`
                  : null
            }
          />
        </div>

        {/* 2. Prírez — ako dielec leží na doske, ktorú stroj upne (KOLO 47),
               plus dôvod prírezu textom (KOLO 49 — predtým celoširoký pás).
               Vizuál je len keď existuje prírez; text sa musí zobraziť aj bez
               neho, inak by sa upozornenie stratilo. */}
        {maPrirezNahlad || cncMaDovod || dielec.prirez_mimo_tabule ? (
          <div className="flex flex-col overflow-y-auto">
            <SectionCompact title="Prírez">
              {/* Prírez sa nezmestí na tabuľu (KOLO 193) — dielec z importu;
                  ručne sa taký neuloží a Cut Rite ho nepustí */}
              {dielec.prirez_mimo_tabule ? (
                <div
                  role="alert"
                  className="flex items-start gap-1.5 text-[10px] leading-tight text-destructive"
                >
                  <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                  <span>{dielec.prirez_mimo_tabule}</span>
                </div>
              ) : null}
              {cncInfo?.prirezy[0] ? (
                <PrirezVisual
                  prirez={cncInfo.prirezy[0]}
                  cistaX={xNum}
                  cistaY={yNum}
                  nadmieraXMm={cncInfo.nadmiera.x}
                  nadmieraYMm={cncInfo.nadmiera.y}
                  kod={dielec.kod}
                  smerVlakien={fields.smer_vlakien}
                  kompaktny
                  sirkaPx={190}
                />
              ) : null}
              {cncMaDovod && cncInfo ? (
                <CncDovodText
                  cncInfo={cncInfo}
                  cnc={cnc}
                  hrubyPrvy={hrubyPrvy}
                  hrubyDruhy={hrubyDruhy}
                  pocetKs={pocetKsNum}
                />
              ) : null}
              {cncInfo?.prirezy[0] ? (
                <>
                  {/* Jeden riadok namiesto dvoch — prefix „prírez" je
                      zbytočný pod hlavičkou „Prírez" (KOLO 55). */}
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {normalizeNum(cncInfo.prirezy[0].x)}×
                    {normalizeNum(cncInfo.prirezy[0].y)} mm ·{" "}
                    {cncInfo.kusovVPrireze > 1
                      ? `${cncInfo.kusovVPrireze} ks/prírez`
                      : "1 ks/prírez"}{" "}
                    · {pocetKsNum} ks → {cncInfo.pocetPrirezov}×
                  </div>
                  <PrirezLegenda kompaktna />
                </>
              ) : null}
            </SectionCompact>
          </div>
        ) : null}

        {/* 3. Spracovanie + Materiál prvku (vedľa seba) + chipy slotov.
               `overflow-hidden`, NIE `overflow-y-auto` (KOLO 100) — rovnaký
               dôvod ako pri nákrese v KOLE 66: `overflow-y: auto` vynúti aj
               `overflow-x: auto`, zvislý scrollbar zožerie ~15 px šírky
               a pribudne vodorovný.
               Rozpočet výšky: karta 313 − 2 (bordery) − 53 (hlavička) = 258.
               Do KOLA 100 boli tri sekcie pod sebou (~375–420 px) a stĺpec
               reálne scrolloval. Teraz: max(Spracovanie 140, Materiál 141)
               + 1 + chipy (31 + 24…72) = 197 pri 1920 px, 245 pri 1366 px.
               Miesto na dva podstĺpce je až odvtedy, čo editor stratil
               strop `container`u — track má 358 px pri 1366 a ~660 pri 1920. */}
        <div className="flex flex-col overflow-hidden">
          <div className="lg:grid lg:grid-cols-2 lg:divide-x">
          {/* Spracovanie — nastavenia, ktoré sa nemenia pri každom dielci
              (KOLO 67). Prečo tu a nie v karte PRÍREZ: tá sa renderuje len
              pri CNC dielci, takže checkbox nadmiery by zmizol práve vtedy,
              keď ho treba ZAPNÚŤ. Materiálový stĺpec je vždy prítomný. */}
          <SectionCompact title="Spracovanie">
            <div className="space-y-1.5">
              <div>
                <Label
                  htmlFor="d-operacia"
                  title="Cez akú dielňu dielec ide. Zoznam si píšeš v Nastaveniach → Výroba a CNC → Výrobné postupy."
                  className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
                >
                  Výrobný postup
                </Label>
                {/* + Pridať operáciu je VEDĽA poľa, nie pod ním.
                    Postup sa od KOLA 108 **vyberá**, nepíše — voľný text
                    tu padal do `nazov_volny`, teda do názvu dielca. */}
                <div className="flex items-center gap-1">
                  <select
                    id="d-operacia"
                    value={fields.operacia_text}
                    onChange={(e) =>
                      setFields({ ...fields, operacia_text: e.target.value })
                    }
                    className="h-6 w-full rounded border border-input bg-background px-1.5 text-xs"
                  >
                    {/* Sendvič sa vždy lepí (KOLO 138) — len postupy
                        s lepením, bez „—"; backend iný odmietne */}
                    {jeSendvic ? null : <option value="">—</option>}
                    {(jeSendvic
                      ? operacie.filter((o) => jeLepiaciPostup(o.kod))
                      : operacie
                    ).map((o) => (
                      <option key={o.kod} value={o.kod}>
                        {postupPopis(o)}
                      </option>
                    ))}
                    {/* Dielec môže niesť kód, ktorý sa už neponúka
                        (`hw`, `nc_lep_sucho`, alebo odčíslovaný postup).
                        Bez tejto option by `<select>` vykreslil „—",
                        teda klamstvo o tom, čo je v DB — a prvé uloženie
                        formulára by tú hodnotu zahodilo. */}
                    {fields.operacia_text &&
                    !operacie.some((o) => o.kod === fields.operacia_text) ? (
                      <option value={fields.operacia_text}>
                        {fields.operacia_text} (mimo zoznamu)
                      </option>
                    ) : null}
                  </select>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-6 w-6 shrink-0 text-primary"
                    title="Pridať operáciu (vnorený riadok s cenou)"
                    aria-label="Pridať operáciu"
                    onClick={() => setOpDialogOpen(true)}
                  >
                    <IconOperacia className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Hodnoty +X/+Y zostávajú v hlavičke pri poliach X a Y —
                  hodnota sa edituje tam, kam sa pozeráš (KOLO 50). Tu je len
                  zap/vyp, čo je nastavenie, nie rozmer. */}
              {/* Pridané hrany sú v tom istom riadku ako Nadmiera — stĺpec má
                  pevný výškový rozpočet (KOLO 100) a nový riadok by ho
                  pri CNC dielci s rotáciou prekročil (KOLO 169) */}
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
                  <input
                    type="checkbox"
                    checked={fields.nadmiera_aktivna}
                    onChange={(e) =>
                      setFields({ ...fields, nadmiera_aktivna: e.target.checked })
                    }
                    aria-label="Nadmiera aktívna"
                    title="Nadmiera zap/vyp — hodnoty uprav vedľa X a Y"
                    className="h-3.5 w-3.5 accent-primary cursor-pointer"
                  />
                  Nadmiera
                </label>
                {dielec.smie_pridane_hrany ? (
                  <button
                    type="button"
                    onClick={() => setPridaneOpen(true)}
                    title={
                      pridaneHranyPodtext(pridane) ??
                      "Hrana mimo obvodu dielca — napr. olepenie otvoru"
                    }
                    className="h-5 rounded border border-input px-1.5 text-[10px] hover:bg-accent"
                  >
                    {pridane.length > 0
                      ? `Pridané hrany (${pridane.length})`
                      : "+ Pridaná hrana"}
                  </button>
                ) : null}
              </div>

              {/* CNC voľba (auto / áno / nie) odišla v KOLE 140 — user: „načo
                  to tam je?". Či dielec ide na CNC, určuje výrobný postup
                  (`je_cnc`); `ide_na_cnc` ostáva len pre Korpus Builder
                  a zásuvky, ktoré dielcom bez postupu nastavujú CNC samy. */}

              {/* Rotácia kvôli čelám — auto podľa názvu (zas_chrb/zas_celo)
                  alebo manuálny override. Len pri dielcoch idúcich na CNC,
                  ktoré sa zmestia do rotačného priestoru stroja (VBA
                  CanRotate) — väčší dielec sa fyzicky nedá otočiť, tak voľbu
                  ani neponúkame. */}
              {cncInfo?.cncDielec &&
              cnc != null &&
              cnc.rotaciaMaxMm > 0 &&
              Math.max(xNum, yNum) <= cnc.rotaciaMaxMm ? (
                <div>
                  <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
                    Rotácia (čelá)
                  </div>
                  <select
                    value={
                      fields.cnc_rotovat === null
                        ? "auto"
                        : String(fields.cnc_rotovat)
                    }
                    onChange={(e) =>
                      setFields({
                        ...fields,
                        cnc_rotovat:
                          e.target.value === "auto"
                            ? null
                            : e.target.value === "true",
                      })
                    }
                    title={
                      "Otočiť dielec o 90° kvôli operáciám na čelách " +
                      "(kolíkovanie). Auto = podľa názvu (zas_chrb/zas_celo)."
                    }
                    className="h-6 w-full rounded border bg-background px-1 text-xs"
                  >
                    <option value="auto">
                      Auto{rotovat ? " (áno)" : " (nie)"}
                    </option>
                    <option value="true">Áno</option>
                    <option value="false">Nie</option>
                  </select>
                </div>
              ) : null}
            </div>
          </SectionCompact>
          {/* Pod `lg` sú sekcie pod sebou (čiara hore), od `lg` vedľa seba
              a delí ich `divide-x` na gride — inak by tu visel border-t
              cez celý stĺpec. */}
          <div className="border-t lg:border-t-0">
            <SectionCompact title="Materiál prvku">
            {materialPrvku ? (
              <div className="space-y-0.5 text-[11px]">
                <div className="font-mono font-medium text-xs">
                  {materialPrimaryId(materialPrvku) ??
                    materialPrvku.id.slice(0, 8)}
                </div>
                <div className="truncate" title={materialPrvku.nazov}>
                  {materialPrvku.nazov}
                </div>
                <KVRow label="Dekor" value={materialPrvku.dekor_kod ?? "—"} />
                {/* Formát a kategória sú referenčné hodnoty — na vlastný
                    riadok každá nepotrebuje (KOLO 100, −16 px). Hrana
                    formát nemá — miesto dostane Zmeniť hranu. */}
                {riadokZHrany ? null : (
                  <KVRow
                    label="Formát"
                    value={`${
                      materialPrvku.x_mm && materialPrvku.y_mm
                        ? `${materialPrvku.x_mm}×${materialPrvku.y_mm}`
                        : "—"
                    } · ${materialPrvku.kategoria ?? "—"}`}
                  />
                )}
                <CenaMJRow zakazkaId={zakazkaId} material={materialPrvku} />
                {currentSlot ? (
                  <KVRow label="Materiál zákazky" value={currentSlot.slot_kod} />
                ) : null}
                {riadokZHrany ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-1 h-6 text-[11px]"
                    title="Cenovka sa reže z hrany — vyber inú hranu z cenníka"
                    onClick={() => setZmenaHrany(true)}
                  >
                    Zmeniť hranu
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Žiadny materiál priradený. Vyber ho nižšie.
              </p>
            )}
            </SectionCompact>
            </div>
          </div>
          <div className="border-t">
            <SectionCompact title="Materiály v zákazke">
              {sloty.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Žiadne sloty. Pridaj M1, M2, … v editori.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => setSlotId(null)}
                    title="Bez materiálu — odobrať priradený materiál"
                    className={
                      "rounded-full border px-2 py-0.5 text-[11px] font-mono transition-colors " +
                      (slotId === null
                        ? "border-primary bg-tandem-red-light"
                        : "border-input hover:bg-accent")
                    }
                  >
                    (žiadny)
                  </button>
                  {slotyPreDielec(sloty, povodnySlot).map((s) => {
                    // Slot s inou hrúbkou dosky prepíše hrúbku dielca
                    // (KOLO 159 — do vtedy bol čip vypnutý a user musel
                    // najprv prepísať hrúbku). Lepený sendvič (hrúbka so
                    // split rule) si hrúbku nechá a nesie slot vrstvy.
                    const slotZ =
                      s.material?.z_mm != null
                        ? parseFloat(String(s.material.z_mm))
                        : null;
                    const dielecZ = parseFloat(fields.z_mm || "0");
                    const jeSendvic =
                      !dielec.parent_dielec_id &&
                      !!pravidloPreHrubku(splitRules, fields.z_mm);
                    const inaHrubka =
                      slotZ != null && slotZ !== dielecZ && !jeSendvic;
                    return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                setSlotId(s.id);
                setLastSlot(s.varianta_id, s.id);
                // Hrúbka podľa materiálu slotu (aj keď už nejaká je)
                if (inaHrubka) {
                  setFields({ ...fields, z_mm: normalizeNum(slotZ) });
                }
              }}
                      title={
                        inaHrubka
                          ? `${s.material?.nazov ?? ""} — hrúbka dielca sa zmení na ${slotZ} mm`
                          : (s.material?.nazov ?? "(bez materiálu)")
                      }
                      className={
                        "flex items-baseline gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors " +
                        (slotId === s.id
                          ? "border-primary bg-tandem-red-light"
                          : "border-input hover:bg-accent")
                      }
                    >
                      {/* Len kód slotu (KOLO 100): s názvom mal chip
                          ~150 px, takže sedem slotov bolo sedem riadkov
                          a stĺpec pretiekol. Názov je v `title`, o sekciu
                          vyššie v „Materiál prvku" a v paneli v tabe Sloty. */}
                      <span className="font-mono font-medium">{s.slot_kod}</span>
                    </button>
                    );
                  })}
                </div>
              )}
            </SectionCompact>
          </div>
        </div>

        {/* 3. Hrany + kovania v zostave — sumáre.
               Od `xl` (KOLO 63): pás ide cez plnú šírku, takže má pri `xl`
               1232 px a pevné tracky (diagram ~428 + Prírez 260 + tento 250
               = 938) nechajú materiálu 294 px. Do KOLA 62 sedel detail len
               v stĺpci tabuľky (~896 px), tam by materiál spadol do zápornej
               šírky — preto bol gate na `2xl`. Pri `lg` (976 px) by materiál
               dostal 38 px, takže tam štvrtý track stále nie je. Hrany a
               kovania sú aj v pravom paneli, v scope celého výpočtu. */}
        {/* Gate je `2xl`, nie `xl` — v stĺpci tabuľky (894 px) sa štyri
            tracky nezmestia (KOLO 67); musí sedieť s grid-cols vyššie. */}
        <div className="hidden flex-col divide-y overflow-y-auto 2xl:flex">
          <EdgesUsageSection
            zakazkaId={zakazkaId}
            variantaId={sloty[0]?.varianta_id ?? null}
            zostavaId={dielec.zostava_id}
            title="Hrany v zostave"
          />
          <KovaniaUsageSection
            zakazkaId={zakazkaId}
            variantaId={sloty[0]?.varianta_id ?? null}
            zostavaId={dielec.zostava_id}
            title="Kovania v zostave"
          />
        </div>
      </div>
      {lepeny ? (
        <LepenyConvertDialog
          open
          zakazkaId={zakazkaId}
          dielec={dielec}
          rule={lepeny.rule}
          zvolenaZ={lepeny.z}
          vrstvy={vrstvy}
          onClose={() => setLepeny(null)}
        />
      ) : null}
      {opDialogOpen ? (
        <PridatOperaciuDialog
          zakazkaId={zakazkaId}
          dielec={dielec}
          open
          onOpenChange={setOpDialogOpen}
        />
      ) : null}
      {pridaneOpen ? (
        <PridaneHranyDialog
          zakazkaId={zakazkaId}
          dielec={dielec}
          open
          onOpenChange={setPridaneOpen}
          vyber={vyberPridanejHrany}
        />
      ) : null}
      {zmenaHrany ? (
        <HranaPickerDialog
          open
          currentId={dielec.material_id_direct ?? null}
          onClose={() => setZmenaHrany(false)}
          onSelect={(id) => {
            setZmenaHrany(false);
            if (id === dielec.material_id_direct) return;
            updateMutation
              .mutateAsync({ id: dielec.id, data: { material_id_direct: id } })
              .catch((err: unknown) => {
                toast({
                  variant: "destructive",
                  title: "Chyba pri ukladaní",
                  description: extractErrorMessage(err),
                });
              });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Detail vnorenej operácie — len názov, kusy, info o operácii a cena.
 * Množstvo počíta backend z rozmerov rodičovského dielca.
 */
function OperaciaDetailBody({
  zakazkaId,
  dielec,
}: {
  zakazkaId: string;
  dielec: Dielec;
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const deleteMutation = useDeleteDielec(zakazkaId);
  const [ks, setKs] = useState(String(dielec.pocet_ks ?? 1));
  useEffect(() => setKs(String(dielec.pocet_ks ?? 1)), [dielec]);

  const opQuery = useOperacie();
  const op = (opQuery.data?.data ?? []).find((o) => o.kod === dielec.operacia);
  const opCena = op ? parseFloat(String(op.cena_eur_per_mj ?? "0")) || 0 : 0;
  const cena = dielec.cena_dielca ? parseFloat(dielec.cena_dielca) : 0;
  const ksNum = Math.max(1, parseInt(ks, 10) || 1);

  const podDielcom = dielec.parent_dielec_id != null;

  function ulozKs(): void {
    if (!podDielcom && ksNum !== (dielec.pocet_ks ?? 1)) {
      updateMutation.mutate({ id: dielec.id, data: { pocet_ks: ksNum } });
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border bg-card">
      <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1 border-b bg-background-alt/40 px-4 py-1.5">
        <div>
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
            Kód
          </div>
          <div className="font-mono text-sm font-semibold leading-6">
            {dielec.kod ?? "—"}
          </div>
        </div>
        <div className="w-16">
          <Label className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
            Ks
          </Label>
          {/* Operácia pod dielcom ide s jeho počtom (KOLO 193) — server
              vlastný počet neuloží; voľný ostáva len paušál zostavy */}
          <Input
            value={ks}
            onChange={(e) => setKs(e.target.value)}
            onBlur={ulozKs}
            inputMode="numeric"
            readOnly={podDielcom}
            title={podDielcom ? "Počet ide s dielcom — zmeň počet dielca" : undefined}
            className={cn(
              "h-6 text-xs font-mono px-1.5",
              podDielcom && "bg-muted text-muted-foreground",
            )}
            aria-label="Počet ks operácie"
          />
        </div>
        <div className="ml-auto text-right">
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
            Cena
          </div>
          <div className="font-mono text-sm font-semibold leading-6 text-primary">
            {formatEur(cena * ksNum)}
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-4 text-sm">
        <p className="flex items-center gap-1.5 font-medium">
          <IconOperacia className="h-3.5 w-3.5 text-primary/70" />
          {op?.label ?? dielec.nazov_strojovy ?? dielec.operacia}
          <span className="font-mono text-[11px] font-normal text-muted-foreground">
            {dielec.operacia}
          </span>
        </p>
        {op ? (
          <p className="text-xs text-muted-foreground">
            {opCena > 0 && op.mj ? (
              <>
                Sadzba{" "}
                <b className="font-mono">
                  {formatEur(opCena)}/{op.mj}
                </b>
                {parseFloat(String(op.rezia_pct ?? "0")) > 0
                  ? ` + réžia ${normalizeNum(parseFloat(String(op.rezia_pct)))} %`
                  : ""}
                {" — "}množstvo sa počíta z rozmerov dielca (m² = X×Y, m = X)
                a prepočíta sa pri zmene rozmerov.
              </>
            ) : (
              <span className="text-amber-600">
                Operácia nemá nastavenú cenu — uprav ju v Cenníku → Operácie.
              </span>
            )}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Operácia je vnorená pod dielcom — hrany, nadmiera ani materiál sa
          na ňu nevzťahujú.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="hover:text-destructive"
          onClick={() => void deleteMutation.mutateAsync(dielec.id)}
          title="Zmazať túto vnorenú operáciu"
        >
          Zmazať operáciu
        </Button>
      </div>
    </div>
  );
}

/**
 * Detail kovania (kusový dielec) — edituje sa len názov a počet kusov,
 * kovanie samotné sa mení cez KovanieChangeDialog. Rovnaká pevná výška
 * ako plný panel, aby layout neskákal.
 */
function KovanieDetailBody({
  zakazkaId,
  dielec,
  sloty,
}: {
  zakazkaId: string;
  dielec: Dielec;
  sloty: MaterialSlot[];
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const [nazov, setNazov] = useState(dielec.nazov_strojovy ?? "");
  const [ks, setKs] = useState(String(dielec.pocet_ks ?? 1));
  const [zmenaKovania, setZmenaKovania] = useState(false);

  // Pending autosave — flush pri prepnutí dielca (rovnaký vzor ako DetailBody)
  const pendingRef = useRef<{ id: string; data: DielecUpdate } | null>(null);
  const mutateRef = useRef(updateMutation.mutate);
  useEffect(() => {
    mutateRef.current = updateMutation.mutate;
  });
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) mutateRef.current(pending);
    };
  }, [dielec.id]);
  useEffect(() => {
    setNazov(dielec.nazov_strojovy ?? "");
    setKs(String(dielec.pocet_ks ?? 1));
  }, [dielec]);
  useEffect(() => {
    const dirty =
      nazov !== (dielec.nazov_strojovy ?? "") ||
      (parseInt(ks, 10) || 1) !== (dielec.pocet_ks ?? 1);
    if (!dirty) {
      pendingRef.current = null;
      return undefined;
    }
    const pending = {
      id: dielec.id,
      data: {
        nazov_strojovy: nazov || null,
        pocet_ks: parseInt(ks, 10) || 1,
      } as DielecUpdate,
    };
    pendingRef.current = pending;
    const t = setTimeout(() => {
      pendingRef.current = null;
      updateMutation.mutateAsync(pending).catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní",
          description: extractErrorMessage(err),
        });
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dielec, nazov, ks]);

  const m = dielec.material_direct;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border bg-card">
      {/* Hlavičkový pás — kód, názov, kusy, cena */}
      <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1 border-b bg-background-alt/40 px-4 py-1.5">
        <div>
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">Kód</div>
          <div className="font-mono text-sm font-semibold leading-6">
            {dielec.kod ?? "—"}
          </div>
        </div>
        <div>
          <Label
            htmlFor="k-nazov"
            className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
          >
            Názov
          </Label>
          <Input
            id="k-nazov"
            value={nazov}
            onChange={(e) => setNazov(e.target.value)}
            placeholder="Výsuvy / Nohy / …"
            className="h-6 w-44 text-xs px-1.5"
          />
        </div>
        <div className="w-14">
          {/* hint rezerva — rovnaká výška hlavičky ako plný panel (X/Y hint) */}
          <NumField label="Ks" value={ks} onChange={setKs} />
        </div>
      </div>

      {/* Telo: info o kovaní + zmena | kovania v zákazke */}
      <div className="grid min-h-0 flex-1 grid-cols-1 divide-y overflow-y-auto lg:grid-cols-[minmax(0,1fr)_250px] lg:divide-x lg:divide-y-0 lg:overflow-hidden">
        <div className="flex flex-col overflow-y-auto">
          <SectionCompact title="Kovanie">
            {m ? (
              <div className="max-w-xs space-y-0.5 text-[11px]">
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  <IconKusovy className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate" title={m.nazov}>
                    {menoKovania(m).hlavne}
                  </span>
                </div>
                {/* Alias hlavný, pravý názov pod ním (KOLO 176) */}
                <PravyNazov material={m} />
                <KVRow
                  label="ID"
                  value={materialPrimaryId(m) ?? m.id.slice(0, 8)}
                />
                <KVRow
                  label="Kat"
                  value={m.kategoria ? formatKategoria(m.kategoria) : "—"}
                />
                <CenaMJRow zakazkaId={zakazkaId} material={m} />
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Žiadne kovanie priradené.
              </p>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setZmenaKovania(true)}
              className="mt-2 h-7 text-xs"
              title="Vybrať iné kovanie"
            >
              <IconKusovy className="mr-1.5 h-3 w-3" />
              Zmeniť kovanie
            </Button>
          </SectionCompact>
        </div>
        <div className="flex flex-col overflow-y-auto">
          <KovaniaUsageSection
            zakazkaId={zakazkaId}
            variantaId={sloty[0]?.varianta_id ?? null}
            zostavaId={dielec.zostava_id}
            title="Kovania v zostave"
          />
        </div>
      </div>
      {zmenaKovania ? (
        <KovanieChangeDialog
          open
          zakazkaId={zakazkaId}
          dielec={dielec}
          onOpenChange={(o) => {
            if (!o) setZmenaKovania(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** Množstvo súčasti skla podľa druhu materiálu (ks / bm / m²). */
function sucastMnozstvo(s: Dielec): string {
  const druh = s.material_direct?.druh;
  if (druh === "dlzkovy") {
    const bm = (parseFloat(String(s.x_mm ?? "0")) || 0) / 1000;
    return `${normalizeNum(bm)} bm`;
  }
  if (druh === "plosny") {
    const x = parseFloat(String(s.x_mm ?? "0")) || 0;
    const y = parseFloat(String(s.y_mm ?? "0")) || 0;
    return `${((x * y) / 1e6).toFixed(3)} m²`;
  }
  return `${s.pocet_ks ?? 1} ks`;
}

/**
 * Detail skla z kalkulačky — parametre (rozmery, hrúbka, súčasti) sa
 * menia cez dialóg Upraviť sklo, lebo zmena prepočítava aj množstvá
 * súčastí. Inline sa edituje len názov. Rovnaká výška ako plný panel.
 */
function SkloDetailBody({
  zakazkaId,
  dielec,
  sloty,
  sucasti,
  onEditSklo,
}: {
  zakazkaId: string;
  dielec: Dielec;
  sloty: MaterialSlot[];
  sucasti: Dielec[];
  onEditSklo?: () => void;
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const [nazov, setNazov] = useState(dielec.nazov_strojovy ?? "");

  // Pending autosave — flush pri prepnutí dielca (vzor KovanieDetailBody)
  const pendingRef = useRef<{ id: string; data: DielecUpdate } | null>(null);
  const mutateRef = useRef(updateMutation.mutate);
  useEffect(() => {
    mutateRef.current = updateMutation.mutate;
  });
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) mutateRef.current(pending);
    };
  }, [dielec.id]);
  useEffect(() => {
    setNazov(dielec.nazov_strojovy ?? "");
  }, [dielec]);
  useEffect(() => {
    if (nazov === (dielec.nazov_strojovy ?? "")) {
      pendingRef.current = null;
      return undefined;
    }
    const pending = {
      id: dielec.id,
      data: { nazov_strojovy: nazov || null } as DielecUpdate,
    };
    pendingRef.current = pending;
    const t = setTimeout(() => {
      pendingRef.current = null;
      updateMutation.mutateAsync(pending).catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní",
          description: extractErrorMessage(err),
        });
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dielec, nazov]);

  const m = dielec.material_direct;

  function ReadOnly({ label, value }: { label: string; value: string }): JSX.Element {
    return (
      <div>
        <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
          {label}
        </div>
        <div className="font-mono text-xs leading-6">{value}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border bg-card">
      {/* Hlavičkový pás — kód, názov, rozmery (len na čítanie) */}
      <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1 border-b bg-background-alt/40 px-4 py-1.5">
        <div>
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">Kód</div>
          <div className="font-mono text-sm font-semibold leading-6">
            {dielec.kod ?? "—"}
          </div>
        </div>
        <div>
          <Label
            htmlFor="s-nazov"
            className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
          >
            Názov
          </Label>
          <Input
            id="s-nazov"
            value={nazov}
            onChange={(e) => setNazov(e.target.value)}
            className="h-6 w-44 text-xs px-1.5"
          />
        </div>
        <ReadOnly label="Ks" value={String(dielec.pocet_ks ?? 1)} />
        <ReadOnly label="X" value={normalizeNum(dielec.x_mm) || "—"} />
        <ReadOnly label="Y" value={normalizeNum(dielec.y_mm) || "—"} />
        <ReadOnly
          label="Hrúbka"
          value={dielec.z_mm ? `${normalizeNum(dielec.z_mm)} mm` : "—"}
        />
        <div className="ml-auto">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onEditSklo}
            className="h-7 text-xs"
            title="Upraviť rozmery a súčasti skla"
          >
            <Pencil className="mr-1.5 h-3 w-3" />
            Upraviť sklo
          </Button>
        </div>
      </div>

      {/* Telo: sklo + súčasti v cene | hrany + kovania v zostave */}
      <div className="grid min-h-0 flex-1 grid-cols-1 divide-y overflow-y-auto lg:grid-cols-[minmax(0,1fr)_250px] lg:divide-x lg:divide-y-0 lg:overflow-hidden">
        <div className="flex flex-col overflow-y-auto">
          <SectionCompact title="Sklo">
            {m ? (
              <div className="max-w-xs space-y-0.5 text-[11px]">
                <div className="text-xs font-medium truncate" title={m.nazov}>
                  {m.nazov}
                </div>
                <KVRow
                  label="ID"
                  value={materialPrimaryId(m) ?? m.id.slice(0, 8)}
                />
                <CenaMJRow zakazkaId={zakazkaId} material={m} />
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Sklo bez materiálu.
              </p>
            )}
          </SectionCompact>
          <SectionCompact title={`V cene (${sucasti.length})`}>
            {sucasti.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                Bez súčastí — len tabuľa skla.
              </p>
            ) : (
              <ul className="space-y-0.5 text-[11px]">
                {sucasti.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-baseline justify-between gap-2"
                  >
                    <span className="truncate">
                      {s.nazov_strojovy ?? s.kod}
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                      {sucastMnozstvo(s)}
                      {sucastCena(s) > 0
                        ? ` · ${formatEur(sucastCena(s))}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="flex justify-between border-t pt-1 text-[11px]">
              <span className="text-muted-foreground">Cena spolu</span>
              <span className="font-mono font-semibold text-primary">
                {formatEur(skloCenaSpolu(dielec, sucasti))}
              </span>
            </p>
            <p className="pt-1 text-[10px] text-muted-foreground">
              Rozmery a súčasti zmeníš cez „Upraviť sklo".
            </p>
          </SectionCompact>
        </div>
        <div className="flex flex-col divide-y overflow-y-auto">
          <EdgesUsageSection
            zakazkaId={zakazkaId}
            variantaId={sloty[0]?.varianta_id ?? null}
            zostavaId={dielec.zostava_id}
            title="Hrany v zostave"
          />
          <KovaniaUsageSection
            zakazkaId={zakazkaId}
            variantaId={sloty[0]?.varianta_id ?? null}
            zostavaId={dielec.zostava_id}
            title="Kovania v zostave"
          />
        </div>
      </div>
    </div>
  );
}

/** Cena súčasti skla × jej ks. */
function sucastCena(s: Dielec): number {
  const cena = s.cena_dielca ? parseFloat(s.cena_dielca) : 0;
  return cena * (s.pocet_ks ?? 1);
}

/** Cena skla spolu: rodič × ks + Σ súčasť × ks (súčasti sú deti s vlastnou cenou). */
function skloCenaSpolu(dielec: Dielec, sucasti: Dielec[]): number {
  const rodic =
    (dielec.cena_dielca ? parseFloat(dielec.cena_dielca) : 0) *
    (dielec.pocet_ks ?? 1);
  return rodic + sucasti.reduce((sum, s) => sum + sucastCena(s), 0);
}


function SectionCompact({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="px-2.5 py-1.5 space-y-1">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-primary">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

/**
 * Prečo dielec dostal iný prírez, než je jeho čistý rozmer — v karte PRÍREZ
 * pri vizuáli (KOLO 49; predtým celoširoký pás pod hlavičkou).
 *
 * Tri dôvody podľa VBA: predrez (nezmestí sa na stroj) / rotácia kvôli čelám /
 * združenie úzkych kusov. Označenie operácie je spoločné — dielňa podľa neho
 * pozná, čo má na stroji spraviť, a kusovník sa tým nemení.
 *
 * Text je zámerne **jednoriadkový** (KOLO 55): plné vety mali 6–7 riadkov,
 * karta má strop 260 px a začala scrollovať. Rozmer prírezu aj „ks → prírezov"
 * sú aj tak hneď pod týmto blokom, takže sa tu opakovali; celé znenie ostáva
 * v `title` pre toho, kto chce vedieť viac.
 */
function CncDovodText({
  cncInfo,
  cnc,
  hrubyPrvy,
  hrubyDruhy,
  pocetKs,
}: {
  cncInfo: NonNullable<ReturnType<typeof cncStav>>;
  cnc: CncUpnutie | null;
  hrubyPrvy: number;
  hrubyDruhy: number;
  pocetKs: number;
}): JSX.Element {
  const hruby = `${normalizeNum(hrubyPrvy)}×${normalizeNum(hrubyDruhy)} mm`;
  const kratko =
    cncInfo.dovod === "predrez"
      ? `Malý dielec — nezmestí sa na CNC (min ${cnc?.minDlzkaMm}×${cnc?.minSirkaMm})`
      : cncInfo.dovod === "rotacia"
        ? "Rotácia kvôli čelám — prírez cez celú dĺžku"
        : "Združenie — úzke kusy sa režú naraz";
  const plne =
    cncInfo.dovod === "predrez"
      ? `Dielec je menší než minimum upnutia stroja (${cnc?.minDlzkaMm}×${cnc?.minSirkaMm} mm), preto sa reže z väčšieho prírezu ${hruby} a po CNC sa oreže na čistý rozmer.`
      : cncInfo.dovod === "rotacia"
        ? `Dielec má kolíkovanie na čelách, preto sa otočí o 90° a prírez ide cez celú dĺžku ${hruby}.`
        : `Úzke kusy sa režú naraz v jednom príreze: ${cncInfo.prirezy
            .map(
              (p) =>
                `${normalizeNum(p.x)}×${normalizeNum(p.y)}` +
                (p.kusovVPrireze > 1 ? ` (${p.kusovVPrireze} ks)` : ""),
            )
            .join(" + ")}.`;

  return (
    <div className="space-y-0.5 text-[10px] leading-tight">
      <div className="flex items-start gap-1.5" title={plne}>
        {cncInfo.dovod === "predrez" ? (
          <AlertTriangle className="mt-px h-3 w-3 shrink-0 text-warning" />
        ) : (
          <RotateCw className="mt-px h-3 w-3 shrink-0 text-primary" />
        )}
        <span className="min-w-0 font-medium">{kratko}</span>
      </div>
      {/* Označenie operácie pre dielňu (KOLO 45) — samotný kód stačí,
          vysvetlenie je v tooltipe */}
      {cncInfo.oznacenie ? (
        <div
          className="font-mono text-muted-foreground"
          title={`${cncInfo.popisOperacie} — ${pocetKs} ks → ${cncInfo.pocetPrirezov} ${cncInfo.pocetPrirezov === 1 ? "prírez" : "prírezov"}, kusovník sa nemení`}
        >
          {cncInfo.oznacenie}
        </div>
      ) : null}
    </div>
  );
}

function KVRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-2 text-[11px]">
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-mono truncate">{value}</span>
    </div>
  );
}

/** Cena/MJ s per-zákazka overridom (hviezdička = upravená pre zákazku). */
function CenaMJRow({
  zakazkaId,
  material,
}: {
  zakazkaId: string;
  material: { id: string; nakup_cena: string | number; mj: string };
}): JSX.Element {
  const overrideMap = useCenaOverrideMap(zakazkaId);
  const ov = overrideMap.get(material.id);
  const cena = ov?.nakup_cena ?? material.nakup_cena;
  return (
    <KVRow
      label="Cena/MJ"
      value={`${formatEur(cena)}/${material.mj}${ov ? " *" : ""}`}
    />
  );
}

function NumField({
  label,
  value,
  onChange,
  hint,
  extra,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Malý textový riadok pod inputom (rezerva výšky). */
  hint?: string;
  /** Vlastný obsah pod inputom — napr. nadmiera +X s celkovým rozmerom. */
  extra?: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <Label className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
        {label}
      </Label>
      {/* extra (nadmiera) je VEDĽA inputu — pás má jednotnú výšku */}
      <div className="flex items-center gap-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-6 text-xs font-mono px-1.5"
          inputMode="decimal"
        />
        {extra}
      </div>
      {hint ? (
        <div className="flex h-4 items-center text-[9px] font-mono text-muted-foreground">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Kompaktný riadok nadmiery pod X/Y inputom: ±hodnota → celkový rozmer.
 *
 * Nadmiera je od KOLA 104 **znamienková** — základ z operácie mínus
 * odpočet hrán na predfrez, takže pri olepovanom dielci býva záporná
 * (dielec sa olepí a až potom sa reže na čisto). Znak je preto `±`, nie
 * `+`, a mínus sa dá zadať.
 *
 * Kým je `auto`, hodnotu drží appka a pri zmene hrany ju prepočíta;
 * ručný prepis ju uzamkne (rovnaký vzor ako auto-hrany).
 */
function NadmieraMini({
  ariaLabel,
  value,
  onChange,
  total,
  auto,
}: {
  ariaLabel: string;
  value: string;
  onChange: (v: string) => void;
  total: number | null;
  auto: boolean;
}): JSX.Element {
  const znamienko = parseFloat(value) < 0 ? "−" : "±";
  return (
    <>
      <span
        className="text-[10px] leading-none text-muted-foreground"
        title={
          auto
            ? "Nadmieru drží appka — operácia mínus odpočet hrán na predfrez"
            : "Nadmiera je zadaná ručne — appka do nej nesiahne"
        }
      >
        {auto ? znamienko : "✎"}
      </span>
      <Input
        aria-label={ariaLabel}
        title={`${ariaLabel} (mm) — pripočíta sa k rozmeru, mínus uberá${total != null ? `, spolu ${normalizeNum(total)}` : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="decimal"
        className="h-6 w-9 shrink-0 px-1 text-[10px] font-mono"
      />
    </>
  );
}

/**
 * Hrúbka Z — výber len z dostupných hrúbok (dosky z Nastavení + lepené
 * zo split rules). Ak dielec má hrúbku mimo zoznamu (staré dáta), pridá
 * sa ako extra položka, aby select nezobrazoval prázdno.
 */
function HrubkaField({
  value,
  hrubky,
  onChange,
}: {
  value: string;
  hrubky: DostupnaHrubka[];
  onChange: (v: string) => void;
}): JSX.Element {
  const options: DostupnaHrubka[] =
    value && !hrubky.some((h) => String(h.mm) === value)
      ? [...hrubky, { mm: Number(value), lepena: false }].sort(
          (a, b) => a.mm - b.mm,
        )
      : hrubky;
  return (
    <div className="space-y-0.5">
      <Label className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">Z</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger
          className="h-6 w-full px-1.5 text-xs font-mono"
          aria-label="Hrúbka Z"
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((h) => (
            <SelectItem key={h.mm} value={String(h.mm)} className="text-xs">
              {h.mm} mm
              {h.lepena ? ` (lepená${h.vrstvy ? ` ${h.vrstvy.join("+")}` : ""})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Preloží text operácie: kod/label z Nastavení → operacia, inak
 * voľný text → nazov_volny (backend pozná len definované operácie). */
function resolveOperacia(text: string): {
  operacia: string | null;
  nazov_volny: string | null;
} {
  // `nazov_volny: null` sa posiela zámerne: dielec, ktorý voľný text
  // v starom stĺpci Operácia niesol, ho pri prvom uložení stratí —
  // inak by mu v Názve navždy visel reťazec, ktorý tam nikdy nepatril.
  return { operacia: text.trim() || null, nazov_volny: null };
}

/** Hrúbka hrán v osi holého rozmeru (rozmer s hranou − holý), inak 0. */
function hranyVOsi(d: Dielec, pole: PoleRozmeru): number {
  if (!maHolyRozmer(d, pole)) return 0;
  const plny = parseFloat(String(d[pole] ?? ""));
  const holy = parseFloat(String(zobrazenyRozmer(d, pole) ?? ""));
  return Number.isFinite(plny) && Number.isFinite(holy) ? plny - holy : 0;
}

/** Malé číslo pod poľom s holým rozmerom: rozmer po olepení (`502`) —
 * bez slov „s hranou" (KOLO 133; KOLO 132 omylom zmazalo celý podtext). */
function rozmerPoOlepeni(d: Dielec, pole: PoleRozmeru): string | undefined {
  if (hranyVOsi(d, pole) === 0) return undefined;
  return normalizeNum(d[pole]);
}

/**
 * X/Y do PATCH len pri zmene. Rozmer bez holého by holý zmazal (backend:
 * poslaný `x_mm` = rozmer s hranou ako z TopSolidu), takže autosave inej
 * zmeny — hrany, názvu — ho nesmie posielať naspäť.
 */
function rozmeryDoUpdate(
  orig: Dielec,
  fields: DetailFields,
  moznosti: { bezHran: boolean; jeVrstva: boolean },
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const pole of ["x_mm", "y_mm"] as const) {
    if (normalizeNum(fields[pole]) === normalizeNum(zobrazenyRozmer(orig, pole)))
      continue;
    Object.assign(
      out,
      rozmerNaUlozenie(pole, parseDecimal(fields[pole]) || null, moznosti),
    );
  }
  return out;
}

/** Zostav PATCH payload z aktuálneho stavu formulára. */
function buildUpdate(
  orig: Dielec,
  fields: DetailFields,
  hrany: HranyState,
  slotId: string | null,
  zaoblenie: ZaoblenieState,
  moznosti: { bezHran: boolean; jeVrstva: boolean },
): DielecUpdate {
  const op = resolveOperacia(fields.operacia_text);
  return {
    nazov_strojovy: fields.nazov_strojovy || null,
    pocet_ks: parseInt(parseDecimal(fields.pocet_ks), 10) || 1,
    ...rozmeryDoUpdate(orig, fields, moznosti),
    z_mm: parseDecimal(fields.z_mm) || null,
    nadmiera_aktivna: fields.nadmiera_aktivna,
    nadmiera_auto: fields.nadmiera_auto,
    // V poli je zobrazená nadmiera — uloží sa k rozmeru s hranou (KOLO 133)
    nadmiera_x_mm: nadmieraNaUlozenie(
      orig,
      "x",
      parseDecimal(fields.nadmiera_x_mm) || "0",
    ),
    nadmiera_y_mm: nadmieraNaUlozenie(
      orig,
      "y",
      parseDecimal(fields.nadmiera_y_mm) || "0",
    ),
    cnc_rotovat: fields.cnc_rotovat,
    ide_na_cnc: fields.ide_na_cnc,
    operacia: op.operacia,
    nazov_volny: op.nazov_volny,
    smer_vlakien: (fields.smer_vlakien || "X") as "0" | "X" | "Y",
    material_slot_id: slotId,
    // Preklad hrán je v `lib/hrany.ts` — používa ho aj mini-lišta (KOLO 67)
    ...hranyDoUpdate(hrany),
    // Zaoblenie masívu (KOLO 168); na laminát ho backend potichu zahodí
    ...zaoblenieDoUpdate(zaoblenie),
  };
}

function toFields(d: Dielec): DetailFields {
  return {
    nazov_strojovy: d.nazov_strojovy ?? "",
    pocet_ks: String(d.pocet_ks ?? 1),
    // normalizeNum: backend Decimal "500.00" → "500" (zobrazenie aj dirty-check)
    // Holý rozmer, keď ho dielec má — to zadal človek (KOLO 131)
    x_mm: normalizeNum(zobrazenyRozmer(d, "x_mm")),
    y_mm: normalizeNum(zobrazenyRozmer(d, "y_mm")),
    z_mm: normalizeNum(d.z_mm),
    nadmiera_aktivna: d.nadmiera_aktivna ?? true,
    nadmiera_auto: d.nadmiera_auto ?? true,
    // Nadmiera tak, ako ju číta dielňa — pri postupe najprv na CNC
    // k holej doske (KOLO 133)
    nadmiera_x_mm: normalizeNum(zobrazenaNadmiera(d, "x")),
    nadmiera_y_mm: normalizeNum(zobrazenaNadmiera(d, "y")),
    cnc_rotovat: d.cnc_rotovat ?? null,
    ide_na_cnc: d.ide_na_cnc ?? null,
    operacia_text: d.operacia ?? d.nazov_volny ?? "",
    smer_vlakien: d.smer_vlakien ?? "X",
  };
}

function isDirty(
  orig: Dielec,
  fields: DetailFields,
  hrany: HranyState,
  slotId: string | null,
  zaoblenie: ZaoblenieState,
): boolean {
  if (fields.nazov_strojovy !== (orig.nazov_strojovy ?? "")) return true;
  if (parseInt(fields.pocet_ks, 10) !== (orig.pocet_ks ?? 1)) return true;
  // porovnávaj normalizovane — "500" vs "500.00" nie je zmena
  if (normalizeNum(fields.x_mm) !== normalizeNum(zobrazenyRozmer(orig, "x_mm")))
    return true;
  if (normalizeNum(fields.y_mm) !== normalizeNum(zobrazenyRozmer(orig, "y_mm")))
    return true;
  if (normalizeNum(fields.z_mm) !== normalizeNum(orig.z_mm)) return true;
  if (fields.nadmiera_aktivna !== (orig.nadmiera_aktivna ?? true)) return true;
  if (
    normalizeNum(fields.nadmiera_x_mm) !==
    normalizeNum(zobrazenaNadmiera(orig, "x"))
  )
    return true;
  if (
    normalizeNum(fields.nadmiera_y_mm) !==
    normalizeNum(zobrazenaNadmiera(orig, "y"))
  )
    return true;
  if (fields.cnc_rotovat !== (orig.cnc_rotovat ?? null)) return true;
  if (fields.ide_na_cnc !== (orig.ide_na_cnc ?? null)) return true;
  if (fields.operacia_text.trim() !== (orig.operacia ?? orig.nazov_volny ?? ""))
    return true;
  if (fields.smer_vlakien !== (orig.smer_vlakien ?? "X")) return true;
  if (slotId !== (orig.material_slot_id ?? null)) return true;
  if (zaoblenieSaZmenilo(orig, zaoblenie)) return true;
  // Porovnanie hrán je v `lib/hrany.ts` — rovnaké mapovanie ako zápis
  return hranySaZmenili(orig, hrany);
}

export { defaultHranyState };
